import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaUsers, FaSearch, FaFilter, FaSpinner, FaUserMd, FaEdit, FaTimes, FaFileAlt, FaShieldAlt, FaStethoscope } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { adminAPI } from '../../services/adminAPI';

const PLAN_TYPES = [
  { value: '', label: 'All Plans' },
  { value: 'CORE', label: 'Core Program' },
  { value: 'MEDICAL', label: 'Medical Program' },
  { value: 'GLP1', label: 'GLP-1 Program' },
];

const ClientManagement = () => {
  const [clients, setClients] = useState([]);
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [assigning, setAssigning] = useState(false);

  // Filters
  const [filters, setFilters] = useState({
    coachId: '',
    planType: '',
    search: '',
  });

  useEffect(() => {
    fetchInitialData();
  }, []);

  // Fetch clients whenever filters change
  useEffect(() => {
    const fetchFilteredClients = async () => {
      try {
        const data = await adminAPI.getAllClients(filters);
        setClients(data);
      } catch (error) {
        console.error('Failed to fetch clients:', error);
      }
    };

    // Only fetch if initial load is done
    if (!loading) {
      fetchFilteredClients();
    }
  }, [filters, loading]);

  const fetchInitialData = async () => {
    try {
      const [clientsData, coachesData] = await Promise.all([
        adminAPI.getAllClients({}),
        adminAPI.getAllCoaches(),
      ]);
      setClients(clientsData);
      setCoaches(coachesData);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Filters already trigger useEffect, no need to manually fetch
  };

  const openAssignModal = (client) => {
    setSelectedClient(client);
    setShowAssignModal(true);
  };

  const handleAssignCoach = async (coachId) => {
    setAssigning(true);
    try {
      const updatedClient = await adminAPI.assignCoachToClient(
        selectedClient.id,
        coachId || null
      );
      setClients(clients.map(c => c.id === selectedClient.id ? updatedClient : c));
      setShowAssignModal(false);
      toast.success(coachId ? 'Coach assigned successfully' : 'Coach unassigned successfully');
    } catch (error) {
      console.error('Failed to assign coach:', error);
      toast.error('Failed to assign coach');
    } finally {
      setAssigning(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  const getPlanTypeBadgeClass = (planType) => {
    switch (planType) {
      case 'CORE':
        return 'bg-blue-100 text-blue-800';
      case 'MEDICAL':
        return 'bg-purple-100 text-purple-800';
      case 'GLP1':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800';
      case 'EXPIRED':
        return 'bg-red-100 text-red-800';
      case 'CANCELLED':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-yellow-100 text-yellow-800';
    }
  };

  const displayedClients = filters.planType
    ? clients.filter(c =>
        c.planId?.toUpperCase().includes(filters.planType) ||
        c.planName?.toUpperCase().includes(filters.planType)
      )
    : clients;

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-white">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center">
            <FaShieldAlt className="text-2xl text-primary-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-gray-500">Manage coaches, clients, and documents</p>
          </div>
        </div>

        {/* Navigation Tabs */}
        <div className="flex gap-2 mb-8 bg-white rounded-xl p-2 shadow-lg">
          <Link
            to="/admin"
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <FaFileAlt />
            Documents
          </Link>
          <Link
            to="/admin/coaches"
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <FaUserMd />
            Coaches
          </Link>
          <Link
            to="/admin/clients"
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium bg-primary-600 text-white"
          >
            <FaUsers />
            Clients
          </Link>
          <Link
            to="/admin/doctors"
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <FaStethoscope />
            Doctors
          </Link>
        </div>

        {/* Page Header */}
        <h2 className="text-xl font-bold text-gray-900 mb-6">Client Management</h2>

        {/* Filters */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center gap-2 mb-4">
            <FaFilter className="text-primary-600" />
            <h3 className="font-semibold text-gray-900">Filters</h3>
          </div>

          <form onSubmit={handleSearch} className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Coach Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Coach</label>
              <select
                value={filters.coachId}
                onChange={(e) => setFilters({ ...filters, coachId: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                <option value="">All Coaches</option>
                <option value="unassigned">Unassigned</option>
                {coaches.map((coach) => (
                  <option key={coach.id} value={coach.id}>
                    {coach.firstName} {coach.lastName}
                  </option>
                ))}
              </select>
            </div>

            {/* Plan Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Plan Type</label>
              <select
                value={filters.planType}
                onChange={(e) => setFilters({ ...filters, planType: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              >
                {PLAN_TYPES.map((type) => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
              <input
                type="text"
                placeholder="Name or email..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              />
            </div>

            {/* Search Button */}
            <div className="flex items-end">
              <button
                type="submit"
                className="w-full flex items-center justify-center gap-2 bg-primary-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-primary-700 transition-colors"
              >
                <FaSearch />
                Search
              </button>
            </div>
          </form>
        </div>

        {/* Clients Table */}
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <FaSpinner className="animate-spin text-3xl text-gray-400" />
            </div>
          ) : displayedClients.length === 0 ? (
            <div className="text-center py-12 px-4">
              <FaUsers className="text-5xl text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-gray-700 mb-2">No Clients Found</h3>
              <p className="text-gray-500">Try adjusting your filters</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-gray-600">Client</th>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-gray-600">Coach</th>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-gray-600">Plan</th>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-gray-600">Status</th>
                    <th className="text-left py-4 px-6 text-sm font-semibold text-gray-600">Joined</th>
                    <th className="text-right py-4 px-6 text-sm font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {displayedClients.map((client) => (
                    <tr key={client.id} className="hover:bg-gray-50">
                      <td className="py-4 px-6">
                        <div>
                          <p className="font-medium text-gray-900">
                            {client.firstName} {client.lastName}
                          </p>
                          <p className="text-sm text-gray-500">{client.email}</p>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        {client.coach ? (
                          <div className="flex items-center gap-2">
                            <FaUserMd className="text-primary-500" />
                            <span className="text-gray-900">{client.coach.firstName} {client.coach.lastName}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400 italic">Unassigned</span>
                        )}
                      </td>
                      <td className="py-4 px-6">
                        {client.planName ? (
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPlanTypeBadgeClass(client.planId)}`}>
                            {client.planName}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="py-4 px-6">
                        {client.subscriptionStatus ? (
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeClass(client.subscriptionStatus)}`}>
                            {client.subscriptionStatus}
                          </span>
                        ) : (
                          <span className="text-gray-400">No Subscription</span>
                        )}
                      </td>
                      <td className="py-4 px-6 text-sm text-gray-600">
                        {formatDate(client.createdAt)}
                      </td>
                      <td className="py-4 px-6 text-right">
                        <button
                          onClick={() => openAssignModal(client)}
                          className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 font-medium text-sm"
                        >
                          <FaEdit />
                          Assign Coach
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Results Count */}
          {!loading && displayedClients.length > 0 && (
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100">
              <p className="text-sm text-gray-600">
                Showing <span className="font-medium">{displayedClients.length}</span> of <span className="font-medium">{clients.length}</span> clients
              </p>
            </div>
          )}
        </div>

        {/* Assign Coach Modal */}
        {showAssignModal && selectedClient && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full">
              <div className="flex items-center justify-between p-6 border-b">
                <h2 className="text-xl font-bold text-gray-900">Assign Coach</h2>
                <button
                  onClick={() => setShowAssignModal(false)}
                  className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                >
                  <FaTimes />
                </button>
              </div>

              <div className="p-6">
                <div className="mb-6 p-4 bg-gray-50 rounded-xl">
                  <p className="text-sm text-gray-500">Client</p>
                  <p className="font-semibold text-gray-900">
                    {selectedClient.firstName} {selectedClient.lastName}
                  </p>
                  <p className="text-sm text-gray-600">{selectedClient.email}</p>
                </div>

                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-700 mb-3">Select Coach</p>

                  {/* Unassign Option */}
                  <button
                    onClick={() => handleAssignCoach(null)}
                    disabled={assigning}
                    className={`w-full text-left p-4 rounded-xl border-2 transition-colors ${
                      !selectedClient.coachId
                        ? 'border-primary-500 bg-primary-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <p className="font-medium text-gray-900">No Coach (Unassigned)</p>
                    <p className="text-sm text-gray-500">Remove coach assignment</p>
                  </button>

                  {/* Coach Options */}
                  {coaches.map((coach) => (
                    <button
                      key={coach.id}
                      onClick={() => handleAssignCoach(coach.id)}
                      disabled={assigning}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-colors ${
                        selectedClient.coachId === coach.id
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-full flex items-center justify-center text-white font-bold">
                          {coach.firstName?.charAt(0)}{coach.lastName?.charAt(0)}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {coach.firstName} {coach.lastName}
                          </p>
                          <p className="text-sm text-gray-500">
                            {coach.specialization || coach.email}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>

                {assigning && (
                  <div className="flex items-center justify-center gap-2 mt-4 text-primary-600">
                    <FaSpinner className="animate-spin" />
                    <span>Assigning...</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default ClientManagement;
